import {
  ApplicationConfig,
  provideAppInitializer,
  provideEnvironmentInitializer,
  provideBrowserGlobalErrorListeners,
  isDevMode,
  inject,
} from '@angular/core';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { TitleStrategy, provideRouter, withInMemoryScrolling } from '@angular/router';
import {
  MsalInterceptor,
  MsalService,
  MsalBroadcastService,
  MsalGuard,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import {
  Translation,
  TranslocoLoader,
  TranslocoService,
  provideTransloco,
} from '@jsverse/transloco';

import { routes } from './app.routes';
import {
  msalGuardConfigFactory,
  msalInstanceFactory,
  msalInterceptorConfigFactory,
} from './core/auth/auth-config';
import { provideServiceWorker } from '@angular/service-worker';
import {
  DEFAULT_LANGUAGE,
  PREFERRED_LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
  isSupportedLanguage,
} from './core/i18n/locale';
import { TranslatedTitleStrategy } from './core/i18n/translated-title.strategy';

class HttpTranslocoLoader implements TranslocoLoader {
  private readonly http = inject(HttpClient);

  getTranslation(lang: string) {
    return this.http.get<Translation>(`/i18n/${lang}.json`);
  }
}

function detectInitialLanguage(): SupportedLanguage {
  try {
    const saved =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem(PREFERRED_LANGUAGE_STORAGE_KEY)
        : null;
    if (isSupportedLanguage(saved)) return saved;
  } catch {
    // Fall back to the application default when storage is unavailable.
  }

  return DEFAULT_LANGUAGE;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
    },
    provideAppInitializer(() => {
      const instance = inject(MSAL_INSTANCE);
      return instance.initialize();
    }),
    provideTransloco({
      config: {
        availableLangs: [...SUPPORTED_LANGUAGES],
        defaultLang: DEFAULT_LANGUAGE,
        fallbackLang: DEFAULT_LANGUAGE,
        reRenderOnLangChange: true,
        missingHandler: { useFallbackTranslation: true },
        prodMode: !isDevMode(),
      },
      loader: HttpTranslocoLoader,
    }),
    provideEnvironmentInitializer(() => {
      inject(TranslocoService).setActiveLang(detectInitialLanguage());
    }),
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    MsalService,
    MsalBroadcastService,
    MsalGuard,
    provideServiceWorker('OneSignalSDKWorker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
  ],
};
